import React, { useState, useEffect } from 'react';
import { auth, database } from './firebase';
import { ref, get, onValue, set, update } from 'firebase/database';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { useImpersonation } from './ImpersonationContext';

function VenuesManagement() {
  const [venues, setVenues] = useState({});
  const [loading, setLoading] = useState(true); // Start as true
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [cuescoreUsername, setCuescoreUsername] = useState('');
  const [hasCredentials, setHasCredentials] = useState(false); // Track if credentials exist
  const [lastSync, setLastSync] = useState(null);
  const [expandedVenues, setExpandedVenues] = useState({});
  const [revealedCodes, setRevealedCodes] = useState({}); // Track which codes are revealed
  const [showQRCodes, setShowQRCodes] = useState({}); // Track which QR codes to show

  // Impersonation context
  const { effectiveUserId } = useImpersonation();

  useEffect(() => {
    const loadData = async () => {
      const user = auth.currentUser;
      if (!user || !effectiveUserId) {
        setLoading(false);
        return;
      }

      // Load CueScore username for effective user
      const usernameRef = ref(database, `organisers/${effectiveUserId}/cuescoreUsername`);
      const usernameSnapshot = await get(usernameRef);
      if (usernameSnapshot.exists()) {
        setCuescoreUsername(usernameSnapshot.val());
      }

      // Check if credentials exist
      const credentialsRef = ref(database, `organisers/${effectiveUserId}/cuescoreCredentials`);
      const credentialsSnapshot = await get(credentialsRef);
      if (credentialsSnapshot.exists()) {
        const creds = credentialsSnapshot.val();
        // Check if they have both username and password (encrypted or plain)
        setHasCredentials(!!(creds.username && (creds.encryptedPassword || creds.password)));
      }

      // Load last sync time
      const syncRef = ref(database, `organisers/${effectiveUserId}/lastVenueSync`);
      const syncSnapshot = await get(syncRef);
      if (syncSnapshot.exists()) {
        setLastSync(new Date(syncSnapshot.val()));
      }

      // Listen to venues in real-time
      const venuesRef = ref(database, `organisers/${effectiveUserId}/venues`);
      onValue(venuesRef, (snapshot) => {
        console.log('Venues snapshot exists:', snapshot.exists());
        console.log('Venues data:', snapshot.val());
        if (snapshot.exists()) {
          const venuesData = snapshot.val();
          setVenues(venuesData);
          
          // Write scoreboard code lookup index for tablet manual entry
          const codeUpdates = {};
          Object.entries(venuesData).forEach(([venueId, venue]) => {
            if (venue.tables) {
              Object.values(venue.tables).forEach((table) => {
                if (table.scoreboardCode) {
                  codeUpdates[`scoreboardCodes/${table.scoreboardCode.toLowerCase()}`] = {
                    tableId: table.tableId,
                    tableNumber: table.number,
                    venueName: venue.name,
                    organiserId: effectiveUserId
                  };
                }
              });
            }
          });
          if (Object.keys(codeUpdates).length > 0) {
            update(ref(database), codeUpdates).catch(err => console.log('Scoreboard index update:', err));
          }
        } else {
          setVenues({});
        }
        setLoading(false);
      });
    };

    loadData();
  }, [effectiveUserId]);

  const handleSyncVenues = async () => {
    // Check for credentials first
    if (!hasCredentials) {
      setError('Please enter your CueScore credentials in "CueScore Account Settings" first.');
      return;
    }

    if (!cuescoreUsername) {
      setError('Please enter your CueScore username in "CueScore Account Settings" first.');
      return;
    }

    setSyncing(true);
    setError('');

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Get Firebase ID token for authentication
      const idToken = await user.getIdToken();

      // Call Cloud Function
      const response = await axios.post(
        'https://us-central1-cue-clock-pro.cloudfunctions.net/syncCueScoreVenues',
        {
          cuescoreUsername: cuescoreUsername,
          userId: effectiveUserId,
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        console.log('Sync successful!', response.data);
        setLastSync(new Date());
      } else {
        console.log('Sync failed:', response.data);
        setError('Failed to sync venues');
      }
    } catch (err) {
      console.error('Error syncing venues:', err);
      if (err.response?.status === 403) {
        setError(err.response.data.error || 'Authentication required. Please ensure your CueScore credentials are correct.');
      } else {
        setError('Failed to sync venues. Make sure you have access to CueScore Pro.');
      }
    } finally {
      setSyncing(false);
    }
  };

  const toggleVenue = (venueId) => {
    setExpandedVenues((prev) => ({
      ...prev,
      [venueId]: !prev[venueId],
    }));
  };

  const handleClearAllTablets = async () => {
    if (!window.confirm('Clear all tablet sessions? This will log out all tablets.')) {
      return;
    }

    try {
      const tabletsRef = ref(database, 'tablets');
      await set(tabletsRef, null);
      alert('All tablet sessions cleared!');
    } catch (err) {
      console.error('Error clearing tablets:', err);
      alert('Failed to clear tablet sessions');
    }
  };

  const toggleCodeReveal = (tableId) => {
    setRevealedCodes((prev) => ({
      ...prev,
      [tableId]: !prev[tableId],
    }));
  };

  const copyCode = async (code, tableNumber) => {
    try {
      await navigator.clipboard.writeText(code);
      // You could add a toast notification here
      alert(`Table ${tableNumber} code copied: ${code}`);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const styles = {
    card: {
      backgroundColor: '#1f2937',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    title: {
      fontSize: '20px',
      fontWeight: 'bold',
    },
    syncButton: {
      padding: '10px 20px',
      backgroundColor: '#2563eb',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'pointer',
    },
    syncButtonDisabled: {
      padding: '10px 20px',
      backgroundColor: '#4b5563',
      color: '#9ca3af',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'not-allowed',
    },
    lastSync: {
      fontSize: '12px',
      color: '#9ca3af',
      marginBottom: '16px',
    },
    error: {
      padding: '12px',
      backgroundColor: '#7f1d1d',
      color: '#fca5a5',
      borderRadius: '8px',
      fontSize: '14px',
      marginBottom: '16px',
    },
    venueCard: {
      backgroundColor: '#374151',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      cursor: 'pointer',
    },
    venueHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    venueName: {
      fontSize: '18px',
      fontWeight: 'bold',
    },
    venueInfo: {
      fontSize: '14px',
      color: '#9ca3af',
      marginTop: '4px',
    },
    tablesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '12px',
      marginTop: '16px',
    },
    tableCard: {
      backgroundColor: '#1f2937',
      padding: '12px',
      borderRadius: '8px',
      border: '2px solid #4b5563',
    },
    tableNumber: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    tableName: {
      fontSize: '12px',
      color: '#9ca3af',
      marginBottom: '8px',
    },
    tableCode: {
      fontSize: '12px',
      fontFamily: 'monospace',
      backgroundColor: '#111827',
      padding: '4px 8px',
      borderRadius: '4px',
      color: '#60a5fa',
    },
    tableStatus: {
      fontSize: '12px',
      marginTop: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    statusDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#6b7280',
    },
    empty: {
      textAlign: 'center',
      padding: '40px',
      color: '#9ca3af',
    },
  };

  // Always render the full component with sync button
  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h2 style={styles.title}>🏢 Venues & Tables</h2>
        <div style={{display: 'flex', gap: '8px'}}>
          <button
            onClick={handleClearAllTablets}
            style={{...styles.syncButton, backgroundColor: '#dc2626'}}
            title="Clear all tablet sessions (for testing)"
          >
            🧹 Clear Tablets
          </button>
          <button
            onClick={handleSyncVenues}
            style={syncing ? styles.syncButtonDisabled : styles.syncButton}
            disabled={syncing}
          >
            {syncing ? '🔄 Syncing...' : '🔄 Sync Venues'}
          </button>
        </div>
      </div>

      {lastSync && (
        <div style={styles.lastSync}>
          Last synced: {lastSync.toLocaleString()}
        </div>
      )}

      {error && <div style={styles.error}>⚠️ {error}</div>}

      {loading ? (
        <div style={styles.empty}>Loading venues...</div>
      ) : Object.keys(venues).length === 0 ? (
        <div style={styles.empty}>
          {!hasCredentials ? (
            <>
              No venues found. Please enter your CueScore credentials in "CueScore Account Settings" above, then click "Sync Venues".
            </>
          ) : (
            <>
              No venues found. Click "Sync Venues" to fetch from CueScore.
            </>
          )}
        </div>
      ) : (
        Object.entries(venues).map(([venueId, venue]) => (
          <div key={venueId} style={styles.venueCard}>
            <div
              style={styles.venueHeader}
              onClick={() => toggleVenue(venueId)}
            >
              <div>
                <div style={styles.venueName}>📍 {venue.name}</div>
                <div style={styles.venueInfo}>
                  {venue.tableCount} {venue.tableCount === 1 ? 'table' : 'tables'}
                </div>
              </div>
              <span style={{fontSize: '20px'}}>
                {expandedVenues[venueId] ? '▲' : '▼'}
              </span>
            </div>

            {expandedVenues[venueId] && venue.tables && (
              <div style={styles.tablesGrid}>
                {Object.values(venue.tables)
                    .sort((a, b) => a.number - b.number)
                    .map((table) => (
                      <div key={table.tableId} style={styles.tableCard}>
                        <div style={styles.tableNumber}>Table {table.number}</div>
                        <div style={styles.tableName}>{table.name}</div>
                        
                        {/* Hidden/Revealed Code */}
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px'}}>
                          <div style={{
                            ...styles.tableCode,
                            flex: 1,
                            fontFamily: revealedCodes[table.tableId] ? 'monospace' : 'inherit',
                          }}>
                            {revealedCodes[table.tableId] ? table.scoreboardCode : '••••••••'}
                          </div>
                          
                          {/* Eye Icon - Toggle Reveal */}
                          <button
                            onClick={() => toggleCodeReveal(table.tableId)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#374151',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '16px',
                            }}
                            title={revealedCodes[table.tableId] ? 'Hide code' : 'Show code'}
                          >
                            {revealedCodes[table.tableId] ? '🙈' : '👁️'}
                          </button>
                          
                          {/* Copy Icon */}
                          <button
                            onClick={() => copyCode(table.scoreboardCode, table.number)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#374151',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '16px',
                            }}
                            title="Copy code"
                          >
                            📋
                          </button>
                          
                          {/* QR Code Icon - Toggle QR */}
                          <button
                            onClick={() => setShowQRCodes({...showQRCodes, [table.tableId]: !showQRCodes[table.tableId]})}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: showQRCodes[table.tableId] ? '#2563eb' : '#374151',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '16px',
                            }}
                            title={showQRCodes[table.tableId] ? 'Hide QR code' : 'Show QR code'}
                          >
                            📱
                          </button>
                        </div>
                        
                        {/* QR Code Display */}
                        {showQRCodes[table.tableId] && (
                          <div style={{
                            marginTop: '16px',
                            padding: '16px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            textAlign: 'center',
                          }}>
                            <QRCodeSVG
                              value={JSON.stringify({
                                type: 'table-login',
                                tableId: table.tableId,
                                tableNumber: table.number,
                                scoreboardCode: table.scoreboardCode,
                                venueName: venue.name,
                                organiserId: effectiveUserId,
                              })}
                              size={200}
                              level="H"
                              includeMargin={true}
                            />
                            <div style={{
                              marginTop: '8px',
                              fontSize: '12px',
                              color: '#374151',
                              fontWeight: 'bold',
                            }}>
                              Scan to login as Table {table.number}
                            </div>
                          </div>
                        )}
                        
                        <div style={styles.tableStatus}>
                          <div style={styles.statusDot}></div>
                          <span>{table.status || 'Offline'}</span>
                        </div>
                      </div>
                    ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default VenuesManagement;